import { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Icon from '../components/atoms/Icon';

const Layout = ({ children }) => {
  const { user, logout, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const navLinks = [
    { path: '/dashboard', label: 'Dashboard' },
    { path: '/anticipos', label: 'Anticipos' },
    { path: '/admin/usuariosweb', label: 'Admin', adminOnly: true },
  ];

  const handleLogout = () => {
    setIsUserMenuOpen(false);
    logout();
  };

  const closeAllMenus = () => {
    setIsUserMenuOpen(false);
    setIsMobileMenuOpen(false);
  };

  const toggleUserMenu = () => {
    setIsUserMenuOpen((prev) => !prev);
    setIsMobileMenuOpen(false);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen((prev) => !prev);
    setIsUserMenuOpen(false);
  };

  const isActivePath = (path) => location.pathname === path;

  return (
    <div className="min-h-screen flex flex-col bg-white text-gray-900">
      {isAuthenticated && (
        <nav
          className="sticky top-0 z-40 backdrop-blur [background:linear-gradient(180deg,rgba(255,255,255,.92),rgba(255,255,255,.80))] shadow-[0_1px_8px_rgba(0,0,0,.06)]"
          role="navigation"
          aria-label="Main"
        >
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
            <div className="flex items-center gap-8">
              <Link to="/" onClick={closeAllMenus} className="flex-shrink-0">
                <img
                  src="/images/LOGO.png"
                  alt="EC GROUP"
                  width={120}
                  height={40}
                  className="block h-12 w-auto"
                />
              </Link>
              <span className="hidden sm:inline-block text-sm sm:text-base font-semibold tracking-wide text-ec-primary-600 select-none">
                Sistema de Pagos
              </span>
              <nav className="hidden md:flex items-center space-x-2">
                {navLinks
                  .filter((link) => !link.adminOnly || user?.isAdmin)
                  .map((link) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      className={`text-sm rounded-md px-3 py-2 transition-all duration-200 focus:outline-none focus-visible:ring-2 font-semibold focus-visible:ring-ec-primary-500 focus-visible:ring-offset-2 ${isActivePath(link.path) ? 'bg-ec-primary-50 text-ec-primary-600' : 'text-gray-600 hover:bg-gray-100 hover:text-ec-primary-600'}`}
                    >
                      {link.label}
                    </Link>
                  ))}
              </nav>
            </div>

            <div className="flex items-center gap-4">
              <div className="relative">
                <button
                  type="button"
                  onClick={toggleUserMenu}
                  aria-expanded={isUserMenuOpen}
                  className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white/70 hover:bg-white shadow-sm hover:shadow-md px-3 py-1.5 transition-all"
                >
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-r from-ec-primary-600 to-ec-primary-700 text-white font-bold text-sm">
                    {user?.razonSocial?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || 'U'}
                  </div>
                  <Icon name="chevron-down" className={`h-4 w-4 text-gray-500 transition-transform duration-200 ${isUserMenuOpen ? 'rotate-180' : ''}`} />
                </button>
                {isUserMenuOpen && (
                  <div role="menu" className="absolute right-0 mt-2 w-64 rounded-xl border border-gray-200 bg-white shadow-lg p-2">
                    <div className="px-3 py-2">
                      <p className="text-xs text-gray-500 truncate"><span className="font-semibold">Correo: </span>{user?.email}</p>
                    </div>
                    <div className="my-1 h-px bg-gray-100" />
                    <button onClick={handleLogout} className="w-full inline-flex items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-ec-primary-700 hover:bg-ec-primary-50 transition-colors">
                      <Icon name="log-out" className="h-4 w-4" />
                      Cerrar sesión
                    </button>
                  </div>
                )}
              </div>

              <div className="md:hidden">
                <button onClick={toggleMobileMenu} className="inline-flex items-center justify-center rounded-md p-2 text-gray-600 hover:bg-gray-100 hover:text-ec-primary-600 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-ec-primary-500" aria-expanded={isMobileMenuOpen}>
                  <span className="sr-only">Abrir menú principal</span>
                  <Icon name={isMobileMenuOpen ? 'x' : 'menu'} className="h-6 w-6" />
                </button>
              </div>
            </div>
          </div>

          {isMobileMenuOpen && (
            <div className="md:hidden border-t border-gray-200">
              <nav className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
                {navLinks
                  .filter((link) => !link.adminOnly || user?.isAdmin)
                  .map((link) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      onClick={closeAllMenus}
                      className={`block rounded-md px-3 py-2 text-base font-medium ${isActivePath(link.path) ? 'bg-ec-primary-50 text-ec-primary-700' : 'text-gray-700 hover:bg-gray-50 hover:text-ec-primary-600'}`}
                    >
                      {link.label}
                    </Link>
                  ))}
              </nav>
            </div>
          )}
          <div className="h-[3px] w-full bg-ec-primary-500" />
        </nav>
      )}

      <main className={isAuthenticated ? 'mx-auto w-full max-w-7xl flex-1 px-4 sm:px-6 lg:px-8 py-6' : 'flex-1'}>
        {children}
      </main>

      <footer className="mt-auto border-t border-gray-100 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-6 text-center">
          <p className="text-xs text-gray-400">© {new Date().getFullYear()} EC CARGO S.A.S. Todos los derechos reservados.</p>
          {process.env.NODE_ENV === 'development' && <p className="mt-2 text-xs text-ec-primary-600">v2.0.0</p>}
        </div>
      </footer>
    </div>
  );
};

export default Layout;